import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge, Button } from "@odingard/design-system";
import { ErrorState, LoadingState, EmptyState } from "@odingard/design-system";
import type { CatalogApplication, CatalogCustomerState } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

export function Trials() {
  const { activeOrg } = useAuth();
  const navigate = useNavigate();
  const orgId = activeOrg?.id ?? "";
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const state = useAsync(
    async () => {
      const [apps, states] = await Promise.all([
        api.catalogApplications({ delivery: "platform", pageSize: 100 }),
        api.catalogState(orgId),
      ]);
      return { apps: apps.applications.filter((a) => a.delivery === "platform"), states: states.states };
    },
    [orgId],
  );

  async function startTrial(slug: string) {
    setBusyKey(slug);
    setNotice(null);
    try {
      const res = await api.startAppTrial(orgId, slug);
      setNotice(res.reused ? "Trial already active for this app." : "Free trial started. Enjoy 14 days.");
      await state.reload();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Could not start the trial.");
    } finally {
      setBusyKey(null);
    }
  }

  const stateByApp = new Map((state.data?.states ?? []).map((s) => [s.slug, s]));
  const apps = state.data?.apps ?? [];
  const trialing = apps.filter((a) => stateByApp.get(a.slug)?.state === "trialing");
  const expired = apps.filter((a) => stateByApp.get(a.slug)?.state === "expired");
  const available = apps.filter((a) => stateByApp.get(a.slug)?.canTrial);

  function row(app: CatalogApplication, cs: CatalogCustomerState) {
    const onTrial = cs.state === "trialing";
    const isExpired = cs.state === "expired";
    const daysLeft = onTrial ? daysUntil(cs.trialEndsAt) : null;
    return (
      <div className="launch-card" key={app.id}>
        <div className="ods-row" style={{ justifyContent: "space-between" }}>
          <div className="launch-card__icon" aria-hidden="true">
            {app.icon}
          </div>
          {onTrial ? (
            <Badge tone="info">{daysLeft != null ? `Trial · ${daysLeft}d left` : "Trial"}</Badge>
          ) : isExpired ? (
            <Badge tone="danger">Trial expired</Badge>
          ) : (
            <Badge tone="neutral">Trial available</Badge>
          )}
        </div>
        <div>
          <h3 style={{ margin: "0 0 6px" }}>{app.name}</h3>
          <p style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)", margin: 0 }}>{app.tagline || app.description}</p>
        </div>
        <div className="ods-row">
          {onTrial && cs.canLaunch && (
            <Button size="sm" onClick={() => navigate(cs.route ?? app.route ?? "/studio")}>
              Launch
            </Button>
          )}
          {cs.canTrial && (
            <Button size="sm" disabled={busyKey === app.slug} onClick={() => startTrial(app.slug)}>
              {busyKey === app.slug ? "Starting…" : "Start free trial"}
            </Button>
          )}
          {(onTrial || isExpired) && cs.canSubscribe && (
            <Link to="/pricing">
              <Button size="sm" variant={onTrial ? "ghost" : "secondary"}>
                Upgrade
              </Button>
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Trials</h1>
          <p>Track active trials and start new ones. Trials run 14 days, then fail closed until you upgrade.</p>
        </div>
        <Link to="/marketplace">
          <Button variant="secondary" size="sm">
            Browse Marketplace
          </Button>
        </Link>
      </div>

      {notice && <div className="banner banner--info">{notice}</div>}
      {state.loading && <LoadingState label="Loading trials…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
      {state.data && (
        <>
          {trialing.length > 0 && (
            <div className="section" style={{ paddingTop: 0 }}>
              <h2 style={{ fontSize: "var(--ods-text-lg)" }}>Active trials</h2>
              <div className="launcher-grid">{trialing.map((a) => row(a, stateByApp.get(a.slug)!))}</div>
            </div>
          )}
          {expired.length > 0 && (
            <div className="section" style={{ paddingTop: 0 }}>
              <h2 style={{ fontSize: "var(--ods-text-lg)" }}>Expired trials</h2>
              <div className="launcher-grid">{expired.map((a) => row(a, stateByApp.get(a.slug)!))}</div>
            </div>
          )}
          {available.length > 0 && (
            <div className="section" style={{ paddingTop: 0 }}>
              <h2 style={{ fontSize: "var(--ods-text-lg)" }}>Available to try</h2>
              <div className="launcher-grid">{available.map((a) => row(a, stateByApp.get(a.slug)!))}</div>
            </div>
          )}
          {trialing.length === 0 && expired.length === 0 && available.length === 0 && (
            <EmptyState
              title="No trials available"
              description="You've already enabled the apps you can trial, or none are eligible right now."
            />
          )}
        </>
      )}
    </>
  );
}
